import React, { Component } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import './CancelLeaveRequestPage.css';
import ConfirmBox from '../../../components/ConfirmBox/ConfirmBox';
import AlertBox from '../../../components/AlertBox/AlertBox';

// Redux actions
import CancelLeaveActions from '../../../redux/CancelLeaveRedux';
import GetLeavesCancelActions from '../../../redux/GetLeavesCancelRedux';

// Icons
import { t } from 'i18next';

interface CancelLeaveRequestPageProps {
  navigate: NavigateFunction;
  // Redux props
  currentSettings: any;
  cancelLeaveFetching: boolean;
  cancelLeaveSuccess: boolean;
  cancelLeaveError: boolean;
  status: any;
  getLeavesCancelFetching: boolean;
  getLeavesCancelSuccess: boolean;
  getLeavesCancelError: boolean;
  listLeavesCancel: any;
  // Redux actions
  cancelLeave: (leave_id: string) => void;
  getLeavesCancel: (children_id: string, page: number) => void;
}

interface CancelLeaveRequestPageState {
  selected: string | null;
  dataLeave: any[];
  page: number;
  total: number | null;
  // App site structure popups
  confirmBoxPopup: boolean;
  alertBoxPopup: boolean;
  alertBoxMessage: string;
  shouldNavigate: boolean;
}

class CancelLeaveRequestPage extends Component<CancelLeaveRequestPageProps, CancelLeaveRequestPageState> {
  constructor(props: CancelLeaveRequestPageProps) {
    super(props);
    
    // Get children_id from currentSettings
    const children_id = this.getChildrenId();
    
    // Fetch leave requests on component mount
    if (children_id) {
      this.props.getLeavesCancel(children_id, 1);
    }
    
    this.state = {
      selected: null,
      dataLeave: [],
      page: 1,
      total: null,
      confirmBoxPopup: false,
      alertBoxPopup: false,
      alertBoxMessage: '',
      shouldNavigate: false,
    };
  }

  componentDidMount() {
    console.log('CancelLeaveRequestPage mounted');
  }

  componentDidUpdate(prevProps: CancelLeaveRequestPageProps) {
    // Handle get leaves cancel success/error
    if (!this.props.getLeavesCancelFetching) {
      if (
        this.props.getLeavesCancelSuccess &&
        this.props.getLeavesCancelSuccess !== prevProps.getLeavesCancelSuccess
      ) {
        this.setState({
          total: this.props.listLeavesCancel?.meta?.total,
        });
        if (this.state.page !== 1) {
          this.setState({
            dataLeave: this.state.dataLeave.concat(
              this.props.listLeavesCancel?.data?.leave_data || []
            ),
          });
        } else {
          this.setState({
            dataLeave: this.props.listLeavesCancel?.data?.leave_data || [],
          });
        }
      }
    }

    // Handle cancel leave success/error
    if (!this.props.cancelLeaveFetching) {
      if (
        this.props.cancelLeaveSuccess &&
        this.props.cancelLeaveSuccess !== prevProps.cancelLeaveSuccess
      ) {
        if (this.props.status?.success === false) {
          this.setState({ 
            alertBoxPopup: true, 
            alertBoxMessage: this.props.status?.message || t?.('leave-request:cancel-failed') || 'Cancel leave failed! Something went wrong',
            shouldNavigate: false
          });
        } else {
          this.setState({ 
            alertBoxPopup: true, 
            alertBoxMessage: t?.('select-leavere-quest:cancel-succes') || 'Cancel leave request successfully',
            shouldNavigate: true
          });
        }
      }

      if (
        this.props.cancelLeaveError &&
        this.props.cancelLeaveError !== prevProps.cancelLeaveError
      ) {
        this.setState({ 
          alertBoxPopup: true, 
          alertBoxMessage: t?.('leave-request:cancel-error') || 'Request submitted failed! Something went wrong',
          shouldNavigate: false
        });
      }
    }
  }

  getChildrenId(): string | null {
    // Try multiple sources for children_id
    let children_id = null;
    
    // 1. Try currentSettings.child.salesforce_contact_id
    if (this.props.currentSettings?.child?.salesforce_contact_id) {
      children_id = this.props.currentSettings.child.salesforce_contact_id;
    }
    
    // 2. Try currentSettings.child.id if salesforce_contact_id is not available
    else if (this.props.currentSettings?.child?.id) {
      children_id = this.props.currentSettings.child.id;
    }
    
    if (!children_id) {
      console.error('CancelLeaveRequestPage: No valid children_id found');
    }
    
    return children_id;
  }


  getData = async () => {
    const children_id = this.getChildrenId();
    if (children_id) {
      this.props.getLeavesCancel(children_id, this.state.page);
    }
  };

  loadData = () => {
    if (this.props.getLeavesCancelFetching) {
      return;
    }
    if (this.state.page <= (this.props.listLeavesCancel?.meta?.last_page || 0)) {
      this.setState({ page: this.state.page + 1 }, this.getData);
    }
  };

  cancel() {
    this.setState({ confirmBoxPopup: true });
  }

  confirmCancel() {
    if (this.state.selected) {
      this.props.cancelLeave(this.state.selected);
    }
  }

  renderFooter = () => {
    if (!this.props.getLeavesCancelFetching) {
      return null;
    }
    return (
      <div className="loading-footer">
        <div className="loading-spinner"></div>
      </div>
    );
  };

  render() {
    const { selected, dataLeave, total } = this.state;
    const { 
      cancelLeaveFetching, 
      getLeavesCancelFetching, 
      getLeavesCancelSuccess 
    } = this.props;

    return (
      <div className="cancelleaverequest-page">
        {/* Header */}
        <header className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
                <button 
                className="header-button back-button" 
                onClick={() => this.props.navigate(-1)}>
                  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                </button>
                <h1 className="header-title">
                  {String(t?.('leave-request-history:leave-request-list') || 'Leave Request List')}
                </h1>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="page-content">
          {getLeavesCancelFetching && dataLeave.length === 0 ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : getLeavesCancelSuccess && dataLeave && dataLeave.length > 0 ? (
            <div className="leave-list">
              {dataLeave.map((item: any, index: number) => (
                <div key={item.id} className="leave-item">
                  <div 
                    className="leave-content"
                    onClick={() => {
                      this.setState({ selected: item?.id });
                    }}
                  >
                    <div className="leave-main-info">
                      <h3 className="leave-title">
                        {String(t?.('verify-request:leave-request-cancel') || 'Leave Request Cancel')} {total ? total - index : ''}
                      </h3>
                      
                      <div className="leave-details">
                        <div className="detail-row">
                          <span className="detail-label">
                            {String(t?.('appointment-summary-screen:summary.item.name') || 'Name')}
                          </span>
                          <span className="detail-value">{item?.name || ""}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">
                            {String(t?.('select-leavere-quest:leave-date') || 'Leave Date')}
                          </span>
                          <span className="detail-value">
                            {item?.date ? moment(item?.date?.date).format("DD MMMM YYYY") : ""}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">
                            {String(t?.('appointment-summary-screen:summary.item.time') || 'Time')}
                          </span>
                          <span className="detail-value">{item?.time || ""}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">
                            {String(t?.('select-leavere-quest:therapist') || 'Therapist')}
                          </span>
                          <span className="detail-value">{item?.therapist || ""}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">
                            {String(t?.('select-leavere-quest:service-type') || 'Service Type')}
                          </span>
                          <span className="detail-value">{item?.service_type || ""}</span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-label">
                            {String(t?.('select-leavere-quest:type-of-leave') || 'Type of Leave')}
                          </span>
                          <span className="detail-value">
                            {item?.type_of_leave == 1
                              ? String(t?.('select-leavere-quest:sick-leave') || 'Sick Leave')
                              : String(t?.('select-leavere-quest:other-leave') || 'Other Leave')}
                          </span>
                        </div>
                        
                        {item?.type_of_leave == 1 && (
                          <div className="detail-row">
                            <span className="detail-label">
                              {String(t?.('select-leavere-quest:sick-note') || 'Sick Note')}
                            </span>
                            <span className="detail-value">{item?.sick_note || ""}</span>
                          </div>
                        )}
                        
                        {item?.exist_make_up_session_date && item.exist_make_up_session_date.length > 0 && (
                          <div className="detail-row">
                            <span className="detail-label">
                              {String(t?.('leave-request-history:make-up-date') || 'Make-up Date')}:
                            </span>
                            <div className="makeup-dates">
                              {item.exist_make_up_session_date.map((makeupItem: any, makeupIndex: number) => (
                                <div key={makeupIndex} className="makeup-date-item">
                                  <span className="detail-value">
                                    {makeupItem?.date ? moment(makeupItem?.date).format("DD MMMM YYYY") : ""}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Radio Button Selection */}
                    <div className="radio-selection">
                      {selected === item?.id ? (
                        <span className="radio-selected"></span>
                      ) : (
                        <span className="radio-unselected"></span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              {this.state.page <= (this.props.listLeavesCancel?.meta?.last_page || 0) - 1 && (
                <div className="load-more-container">
                  <button 
                    className="load-more-button comman-btn w-100"
                    onClick={this.loadData}
                    disabled={getLeavesCancelFetching}
                  >
                    {getLeavesCancelFetching ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
              
              {this.renderFooter()}
            </div>
          ) : (
            <div className="no-leaves">
              <p>{String(t?.('leave-request:no-leaves') || 'No leave requests available')}</p>
            </div>
          )}
        </div>

        {/* Cancel Button */}
        <div className="action-section">
          <button
            className="cancel-button comman-btn w-100"
            onClick={() => selected != null ? this.cancel() : undefined}
            disabled={selected == null}
          >
            {String(t?.('make-up-session:cancel-leave') || 'CANCEL LEAVE')}
          </button>
        </div>


        {/* App Site Structure Popups */}
        <ConfirmBox
          title={t?.('make-up-session:confirm') || 'Confirm'}
          message={t?.('make-up-session:confirm-cancel-message') || 'Are you sure to cancel this session?'}
          confirmAction={() => this.setState({ confirmBoxPopup: false }, this.confirmCancel)}
          cancelAction={() => this.setState({ confirmBoxPopup: false })}
          isVisible={this.state.confirmBoxPopup}
          onClose={() => {}}
          confirmText={String(t?.('verify-request:ok') || 'OK')}
          cancelText={String(t?.('goal-comment-screen:alert.back.cancel') || 'Cancel')}
        />

        <AlertBox
          title={String(t?.('common:message') || 'Message')}
          message={this.state.alertBoxMessage}
          isVisible={this.state.alertBoxPopup}
          onClose={() => {}}
          okAction={() => {
            if (this.state.shouldNavigate) {
              this.props.navigate('/calendar');
            } else {
              this.setState({ alertBoxPopup: false });
            }
          }}
        />
      </div>
    );
  }
}

// Redux mapping
const mapStateToProps = (state: any) => {
  return {
    currentSettings: state.currentSettings,
    cancelLeaveFetching: state.cancelLeave.fetching,
    cancelLeaveSuccess: state.cancelLeave.success,
    cancelLeaveError: state.cancelLeave.error,
    status: state.cancelLeave.leavedata,
    getLeavesCancelFetching: state.getLeavesCancel.fetching,
    getLeavesCancelSuccess: state.getLeavesCancel.success,
    getLeavesCancelError: state.getLeavesCancel.error,
    listLeavesCancel: state.getLeavesCancel.listLeavesCancel,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    cancelLeave: (leave_id: string) =>
      dispatch(CancelLeaveActions.cancelLeaveRequest(leave_id)),
    getLeavesCancel: (children_id: string, page: number) =>
      dispatch(GetLeavesCancelActions.getLeavesCancelRequest(children_id, page)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CancelLeaveRequestPage);
